import { CONFIG } from "@/config-global";
import axios, { endpoints } from "@/utils/axios";
import { useAuthContext } from "@/hooks/use-auth-context";
import { useOwnedWorkspaceId } from "@/actions/workspace";
import { useNotificationSignalR } from "@/hooks/useNotificationSignalR";
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

interface NotificationData {
  id: string;
  type: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  relatedId?: string;
  metadata?: any;
}

interface NotificationContextType {
  notifications: NotificationData[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  loading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuthContext();
  const { workspaceId } = useOwnedWorkspaceId();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Handle real-time notification received
  const handleNotificationReceived = useCallback(
    (notification: NotificationData) => {
      setNotifications((prev) => {
        // Check if notification already exists to prevent duplicates
        const exists = prev.some((n) => n.id === notification.id);
        if (exists) {
          return prev;
        }
        return [notification, ...prev];
      });
      if (!notification.isRead) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    []
  );

  // Handle real-time unread count update
  const handleNotificationCountUpdated = useCallback(
    (data: { unreadCount: number }) => {
      setUnreadCount(data.unreadCount);
    },
    []
  );

  // SignalR connection for notifications
  const { isConnected } = useNotificationSignalR({
    userId: user?.id || "",
    workspaceId,
    onNotificationReceived: handleNotificationReceived,
    onNotificationCountUpdated: handleNotificationCountUpdated,
  });

  // Fetch notifications from API
  const refreshNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {
          params: {
            endpoint: "get-notifications",
            page: 1,
            limit: 50,
          },
        }
      );

      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await axios.get(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {
          params: {
            endpoint: "get-unread-count",
          },
        }
      );

      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  }, [user?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const response = await axios.put(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {},
        {
          params: {
            endpoint: "mark-read",
            id: id,
          },
        }
      );

      if (response.status === 200) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === id
              ? { ...notification, isRead: true }
              : notification
          )
        );
        // Unread count will be updated via SignalR
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await axios.put(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {},
        {
          params: {
            endpoint: "mark-all-read",
          },
        }
      );

      if (response.status === 200) {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, isRead: true }))
        );
        // Unread count will be updated via SignalR
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      const response = await axios.delete(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {
          params: {
            endpoint: "delete-notification",
            id: id,
          },
        }
      );

      if (response.status === 200) {
        setNotifications((prev) =>
          prev.filter((notification) => notification.id !== id)
        );
        // Unread count will be updated via SignalR if needed
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  }, []);

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    try {
      const response = await axios.delete(
        `${CONFIG.serverUrl}${endpoints.notifications}`,
        {
          params: {
            endpoint: "delete-all-notifications",
          },
        }
      );

      if (response.status === 200) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Failed to delete all notifications:", error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      refreshNotifications();
      fetchUnreadCount();
    }
  }, [user?.id, refreshNotifications, fetchUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refreshNotifications,
    loading,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
}
